import axios from 'axios'
import {getExamResultQuestions} from '../../pages/CustomController'
import {Model as InstructorExam} from '../my-course-exam/list/core/_models'

const API_URL = process.env.REACT_APP_API_URL
const COURSE_OUTCOME_AVERAGES_URL = `${API_URL}/Custom/GetCourseOutcomeAverages`

export type CourseOutcomePerformancePoint = {
  id: string
  label: string
  ratio: number
}

export type CourseOutcomePerformanceResult = {
  points: CourseOutcomePerformancePoint[]
  source: 'course-outcome-api' | 'exam-result-fallback'
  partial: boolean
}

type CourseOutcomeApiItem = {
  courseOutcomeID?: number | string | null
  courseOutcomeId?: number | string | null
  courseOutcomeName?: string | null
  courseOutcomeAverage?: number | string | null
}

type ExamResultQuestion = {
  id?: number | string | null
  isCancelled?: boolean
  courseOutcomes?: Array<{
    order?: number | string | null
    name?: string | null
  }>
}

type ExamQuestionAnalysis = {
  examQuestionId?: number | string | null
  respondentCount?: number | string | null
  correctCount?: number | string | null
  difficulty?: number | string | null
}

function finiteNumber(value: unknown) {
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

function normalizeRatio(value: unknown) {
  const number = finiteNumber(value)
  if (number === undefined) {
    return undefined
  }

  const ratio = number > 1 && number <= 100 ? number / 100 : number
  return Math.min(1, Math.max(0, ratio))
}

function extractOutcomeItems(payload: unknown): CourseOutcomeApiItem[] | undefined {
  if (Array.isArray(payload)) {
    return payload as CourseOutcomeApiItem[]
  }

  if (payload && typeof payload === 'object') {
    const wrapped = payload as {data?: unknown}
    if (Array.isArray(wrapped.data)) {
      return wrapped.data as CourseOutcomeApiItem[]
    }
  }

  return undefined
}

function normalizeApiPoints(items: CourseOutcomeApiItem[]) {
  return items.reduce<CourseOutcomePerformancePoint[]>((result, item, index) => {
    const ratio = normalizeRatio(item.courseOutcomeAverage)
    if (ratio === undefined) {
      return result
    }

    const outcomeId = item.courseOutcomeID ?? item.courseOutcomeId ?? index + 1
    result.push({
      id: String(outcomeId),
      label: item.courseOutcomeName?.trim() || `Çıktı ${outcomeId}`,
      ratio,
    })
    return result
  }, [])
}

function extractExamResultPayload(payload: unknown) {
  if (!payload || typeof payload !== 'object') {
    return undefined
  }

  const root = payload as {
    data?: unknown
    questions?: unknown
    summary?: unknown
  }
  const candidate =
    root.data && typeof root.data === 'object'
      ? (root.data as {questions?: unknown; summary?: unknown})
      : root

  return {
    questions: Array.isArray(candidate.questions)
      ? (candidate.questions as ExamResultQuestion[])
      : [],
    analyses:
      candidate.summary &&
      typeof candidate.summary === 'object' &&
      Array.isArray((candidate.summary as {questionAnalyses?: unknown}).questionAnalyses)
        ? (candidate.summary as {questionAnalyses: ExamQuestionAnalysis[]}).questionAnalyses ?? []
        : [],
  }
}

async function loadExamResultFallback(exams: InstructorExam[]) {
  const uniqueExams = Array.from(
    new Map(
      exams
        .map((exam) => [finiteNumber(exam.id), exam] as const)
        .filter((entry): entry is readonly [number, InstructorExam] => entry[0] !== undefined)
    ).values()
  )

  if (uniqueExams.length === 0) {
    return {points: [], partial: false}
  }

  const responses = await Promise.allSettled(
    uniqueExams.map(async (exam) => ({
      exam,
      payload: await getExamResultQuestions(Number(exam.id)),
    }))
  )

  const combined = new Map<
    string,
    {id: string; label: string; weightedTotal: number; totalWeight: number}
  >()
  let successfulExamCount = 0

  responses.forEach((response) => {
    if (response.status !== 'fulfilled') {
      return
    }

    const parsed = extractExamResultPayload(response.value.payload)
    if (!parsed) {
      return
    }

    successfulExamCount += 1
    const analysisByQuestion = new Map(
      parsed.analyses
        .map((analysis) => {
          const id = finiteNumber(analysis.examQuestionId)
          const directDifficulty = normalizeRatio(analysis.difficulty)
          const respondents = finiteNumber(analysis.respondentCount)
          const correct = finiteNumber(analysis.correctCount)
          const derivedDifficulty =
            respondents !== undefined && respondents > 0 && correct !== undefined
              ? normalizeRatio(correct / respondents)
              : undefined
          return id === undefined
            ? undefined
            : ([String(id), directDifficulty ?? derivedDifficulty] as const)
        })
        .filter(
          (entry): entry is readonly [string, number] =>
            entry !== undefined && entry[1] !== undefined
        )
    )

    const perExam = new Map<string, {id: string; label: string; ratios: number[]}>()
    parsed.questions.forEach((question) => {
      if (question.isCancelled) {
        return
      }

      const questionId = finiteNumber(question.id)
      const ratio =
        questionId === undefined ? undefined : analysisByQuestion.get(String(questionId))
      if (ratio === undefined) {
        return
      }

      ;(question.courseOutcomes ?? []).forEach((outcome, index) => {
        const label = outcome.name?.trim() || `Çıktı ${outcome.order ?? index + 1}`
        const id = String(outcome.order ?? label)
        const key = `${id}:${label}`
        const current = perExam.get(key) || {id, label, ratios: []}
        current.ratios.push(ratio)
        perExam.set(key, current)
      })
    })

    const configuredWeight = finiteNumber(response.value.exam.contributionPercentage)
    const weight = configuredWeight !== undefined && configuredWeight > 0 ? configuredWeight : 1

    perExam.forEach((outcome, key) => {
      if (outcome.ratios.length === 0) {
        return
      }

      const average = outcome.ratios.reduce((sum, ratio) => sum + ratio, 0) / outcome.ratios.length
      const current = combined.get(key) || {
        id: outcome.id,
        label: outcome.label,
        weightedTotal: 0,
        totalWeight: 0,
      }
      current.weightedTotal += average * weight
      current.totalWeight += weight
      combined.set(key, current)
    })
  })

  return {
    points: Array.from(combined.values())
      .filter((outcome) => outcome.totalWeight > 0)
      .map((outcome) => ({
        id: outcome.id,
        label: outcome.label,
        ratio: outcome.weightedTotal / outcome.totalWeight,
      }))
      .sort((left, right) => left.label.localeCompare(right.label, 'tr')),
    partial: successfulExamCount < uniqueExams.length,
  }
}

export async function loadCourseOutcomePerformance(
  semesterCourseId: number,
  exams: InstructorExam[]
): Promise<CourseOutcomePerformanceResult> {
  let primaryError: unknown
  let primaryPoints: CourseOutcomePerformancePoint[] | undefined

  try {
    const response = await axios.get<unknown>(COURSE_OUTCOME_AVERAGES_URL, {
      params: {semesterCourseId},
    })
    const items = extractOutcomeItems(response.data)
    if (!items) {
      throw new Error('Öğrenme çıktısı servisi beklenmeyen bir yanıt döndürdü.')
    }
    primaryPoints = normalizeApiPoints(items)
    if (primaryPoints.length > 0) {
      return {points: primaryPoints, source: 'course-outcome-api', partial: false}
    }
  } catch (error) {
    primaryError = error
  }

  const fallback = await loadExamResultFallback(exams)
  if (fallback.points.length > 0) {
    return {
      points: fallback.points,
      source: 'exam-result-fallback',
      partial: fallback.partial,
    }
  }

  if (primaryError) {
    throw primaryError
  }

  return {
    points: primaryPoints ?? [],
    source: 'course-outcome-api',
    partial: false,
  }
}

export function toCourseOutcomeApiItems(points: CourseOutcomePerformancePoint[]) {
  return points.map((point) => ({
    courseOutcomeID: point.id,
    courseOutcomeName: point.label,
    courseOutcomeAverage: point.ratio,
  }))
}
